/**
 * SHA-256 Hash Calculator
 *
 * Independent file integrity verification tool.
 * Users can hash ANY file on their device to get its SHA-256 hash.
 *
 * Use case for FYP:
 * 1. Hash original file BEFORE uploading to CDEaaS
 * 2. Upload and encrypt the file
 * 3. Later, decrypt the file
 * 4. Hash the decrypted file using this same tool
 * 5. Compare both hashes -- if they match, file integrity is verified
 *
 * This provides INDEPENDENT verification that the examiner can trust,
 * because the hashing is done outside the encrypt/decrypt flow.
 */

import React, { useState, useRef } from 'react';

// Load hash history from localStorage
function loadHistory() {
  try {
    const saved = localStorage.getItem('cdeaas_hash_history');
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
}

function saveHistory(history) {
  localStorage.setItem('cdeaas_hash_history', JSON.stringify(history));
}

// Persist last result so it survives page navigation
function loadLastResult() {
  try {
    const saved = localStorage.getItem('cdeaas_hash_last_result');
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
}

function saveLastResult(result) {
  if (result) {
    localStorage.setItem('cdeaas_hash_last_result', JSON.stringify(result));
  } else {
    localStorage.removeItem('cdeaas_hash_last_result');
  }
}

function HashCalculatorPage() {
  const lastResult = loadLastResult();
  const [file, setFile] = useState(lastResult ? { name: lastResult.name, size: lastResult.size } : null);
  const [hash, setHash] = useState(lastResult?.hash || null);
  const [hashing, setHashing] = useState(false);
  const [history, setHistory] = useState(loadHistory);
  const [compareMode, setCompareMode] = useState(false);
  const [compareHash, setCompareHash] = useState('');
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setHash(null);
    setHashing(true);
    setCopied(false);

    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashHex = Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      setHash(hashHex);
      // Save last result so it persists across page navigation
      saveLastResult({ name: selectedFile.name, size: selectedFile.size, hash: hashHex });

      const newHistory = [
        {
          filename: selectedFile.name,
          size: selectedFile.size,
          hash: hashHex,
          timestamp: new Date().toLocaleTimeString(),
          date: new Date().toLocaleDateString(),
        },
        ...history.slice(0, 9), // keep last 10
      ];
      setHistory(newHistory);
      saveHistory(newHistory);
    } catch (err) {
      setHash('Error computing hash: ' + err.message);
    } finally {
      setHashing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      // Trigger through the same handler
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(droppedFile);
      fileInputRef.current.files = dataTransfer.files;
      handleFileSelect({ target: { files: [droppedFile] } });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const copyHash = () => {
    if (hash) {
      navigator.clipboard.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const hashesMatch = compareMode && compareHash.trim().length === 64 && hash
    ? compareHash.trim().toLowerCase() === hash.toLowerCase()
    : null;

  return (
    <div className="page-container admin-page-shell admin-tactile-page">
      <h2 style={s.pageTitle}>SHA-256 Hash Calculator</h2>
      <p style={s.subtitle}>
        Independent file integrity verification. Hash any file to get its unique SHA-256 fingerprint.
      </p>

      {/* How to use */}
      <div style={s.infoBanner}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        <div>
          <strong>How to verify file integrity:</strong>
          <ol style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.8, color: 'var(--text-faint)' }}>
            <li>Hash your original file here <strong>before</strong> uploading to JOSMY</li>
            <li>Note down or copy the hash</li>
            <li>Upload, encrypt, store, then later decrypt the file</li>
            <li>Hash the decrypted file here again</li>
            <li>If both hashes match, the file is identical to the original</li>
          </ol>
        </div>
      </div>

      {/* Drop zone */}
      <div
        style={s.dropzone}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          style={s.hiddenInput}
          id="hash-file-input"
        />
        <label htmlFor="hash-file-input" style={s.dropLabel}>
          {hashing ? (
            <>
              <div style={s.spinner} />
              <span style={{ color: 'var(--blue)', fontWeight: 500 }}>Computing SHA-256 hash...</span>
            </>
          ) : (
            <>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span style={{ fontSize: 14, color: 'var(--text-faint)' }}>
                Drop a file here or click to select
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Any file type, any size. Hashing is done locally in your browser.
              </span>
            </>
          )}
        </label>
      </div>

      {/* Result */}
      {hash && file && (
        <div style={s.resultCard}>
          <div style={s.resultHeader}>
            <div>
              <div style={s.resultFilename}>{file.name}</div>
              <div style={s.resultMeta}>{formatSize(file.size)}</div>
            </div>
            <span className="security-badge green">SHA-256</span>
          </div>

          <div style={s.hashSection}>
            <label style={s.hashLabel}>SHA-256 Hash:</label>
            <div style={s.hashRow}>
              <code style={s.hashCode}>{hash}</code>
              <button style={s.copyBtn} onClick={copyHash}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Compare mode */}
          <div style={s.compareSection}>
            <button
              style={s.compareToggle}
              onClick={() => setCompareMode(!compareMode)}
              type="button"
            >
              {compareMode ? 'Hide comparison' : 'Compare with another hash'}
            </button>

            {compareMode && (
              <div style={{ marginTop: 10 }}>
                <label style={s.hashLabel}>Paste hash to compare:</label>
                <input
                  type="text"
                  value={compareHash}
                  onChange={(e) => setCompareHash(e.target.value)}
                  style={s.compareInput}
                  placeholder="Paste a SHA-256 hash here (64 hex characters)"
                  spellCheck={false}
                />
                {hashesMatch !== null && (
                  <div style={{
                    ...s.compareResult,
                    backgroundColor: hashesMatch ? '#dcfce7' : '#fef2f2',
                    borderColor: hashesMatch ? '#86efac' : '#fecaca',
                    color: hashesMatch ? 'var(--green)' : 'var(--red)',
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke={hashesMatch ? 'var(--green)' : 'var(--red)'}
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      {hashesMatch
                        ? <polyline points="20 6 9 17 4 12"/>
                        : <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                      }
                    </svg>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>
                      {hashesMatch ? 'MATCH -- Files are identical' : 'MISMATCH -- Files are different'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hash History */}
      {history.length > 0 && (
        <div style={s.historySection}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ ...s.historyTitle, margin: 0 }}>Recent Hashes</h3>
            <button
              onClick={() => { setHistory([]); saveHistory([]); setFile(null); setHash(null); saveLastResult(null); }}
              style={{ background: 'none', border: 'none', color: 'var(--red, #dc3545)', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
            >
              Clear History
            </button>
          </div>
          <div style={s.historyList}>
            {history.map((item, i) => (
              <div key={i} style={s.historyItem}>
                <div style={s.historyFilename}>{item.filename}</div>
                <div style={s.historyMeta}>
                  <span>{formatSize(item.size)}</span>
                  <span style={s.historyDot} />
                  <span>{item.date ? `${item.date} ${item.timestamp}` : item.timestamp}</span>
                </div>
                <code style={s.historyHash}>{item.hash}</code>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Technical note */}
      <div style={s.techNote}>
        <strong>Technical Note:</strong> SHA-256 (Secure Hash Algorithm 256-bit) produces a unique
        256-bit (64 hex character) fingerprint for any file. Even a single byte change produces a
        completely different hash. This tool uses the browser's native Web Crypto API -- no data
        leaves your device.
      </div>
    </div>
  );
}

const s = {
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.4px', lineHeight: 1.1,
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-muted)', margin: '0 0 20px', fontWeight: 500 },

  infoBanner: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.07), rgba(6,182,212,0.04))',
    padding: '16px 20px', borderRadius: 16,
    marginBottom: 24, fontSize: 13, color: 'var(--blue)', display: 'flex', gap: 14,
    alignItems: 'flex-start', border: '1.5px solid rgba(99,102,241,0.15)',
    boxShadow: '0 3px 12px rgba(99,102,241,0.05)', backdropFilter: 'blur(10px)',
  },

  dropzone: {
    position: 'relative', marginBottom: 24,
  },
  hiddenInput: {
    position: 'absolute', width: '100%', height: '100%', opacity: 0,
    cursor: 'pointer', top: 0, left: 0, zIndex: 2,
  },
  dropLabel: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
    padding: '48px 28px', border: '2.5px dashed rgba(99,102,241,0.25)', borderRadius: 22,
    background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))',
    cursor: 'pointer', textAlign: 'center',
    transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)',
    boxShadow: 'inset 0 2px 8px rgba(99,102,241,0.03)',
  },
  spinner: {
    width: 32, height: 32, border: '3px solid rgba(99,102,241,0.15)',
    borderTop: '3px solid var(--blue)', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  resultCard: {
    backgroundColor: 'var(--surface-85)', borderRadius: 22,
    border: '1.5px solid rgba(99,102,241,0.1)', padding: 24, marginBottom: 24,
    boxShadow: '0 4px 20px rgba(99,102,241,0.07)',
    backdropFilter: 'blur(12px)',
  },
  resultHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18,
  },
  resultFilename: { fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' },
  resultMeta: { fontSize: 12, color: 'var(--text-muted)', marginTop: 3, fontWeight: 500 },

  hashSection: { marginBottom: 18 },
  hashLabel: { fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', marginBottom: 8, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' },
  hashRow: { display: 'flex', gap: 10, alignItems: 'stretch' },
  hashCode: {
    flex: 1, display: 'block', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all',
    padding: '12px 14px', background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))',
    borderRadius: 14, color: 'var(--text-primary)', border: '1.5px solid rgba(99,102,241,0.1)',
    letterSpacing: '0.3px', lineHeight: 1.6,
  },
  copyBtn: {
    padding: '0 20px', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    border: 'none', borderRadius: 14, cursor: 'pointer', fontSize: 13, fontWeight: 700,
    whiteSpace: 'nowrap', flexShrink: 0, boxShadow: '0 3px 12px rgba(99,102,241,0.25)',
    transition: 'all 0.25s ease',
  },

  compareSection: { borderTop: '1px solid rgba(99,102,241,0.08)', paddingTop: 14 },
  compareToggle: {
    background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer',
    fontSize: 13, fontWeight: 700, padding: 0,
  },
  compareInput: {
    width: '100%', padding: '12px 14px', border: '1.5px solid rgba(99,102,241,0.12)',
    borderRadius: 14, fontSize: 12, fontFamily: 'monospace',
    outline: 'none', boxSizing: 'border-box', marginTop: 8,
    backgroundColor: 'var(--surface-90)', transition: 'all 0.2s ease',
  },
  compareResult: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
    borderRadius: 14, marginTop: 12, border: '1.5px solid',
    fontWeight: 600, fontSize: 13,
  },

  historySection: { marginBottom: 24 },
  historyTitle: { fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 14px' },
  historyList: {
    backgroundColor: 'var(--surface-85)', borderRadius: 20,
    border: '1.5px solid rgba(99,102,241,0.08)', overflow: 'hidden',
    boxShadow: '0 3px 14px rgba(99,102,241,0.05)',
    backdropFilter: 'blur(12px)',
  },
  historyItem: {
    padding: '14px 18px', borderBottom: '1px solid rgba(99,102,241,0.06)',
    transition: 'background-color 0.2s ease',
  },
  historyFilename: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' },
  historyMeta: {
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-secondary)',
    marginTop: 3, marginBottom: 6, fontWeight: 500,
  },
  historyDot: {
    width: 4, height: 4, borderRadius: '50%', backgroundColor: 'rgba(99,102,241,0.3)',
  },
  historyHash: {
    fontSize: 10, fontFamily: 'monospace', color: 'var(--text-muted)', wordBreak: 'break-all',
    lineHeight: 1.4,
  },

  techNote: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.03))',
    padding: '16px 20px', borderRadius: 18,
    fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6,
    border: '1.5px solid rgba(99,102,241,0.1)',
    boxShadow: '0 2px 10px rgba(99,102,241,0.04)',
  },
};

export default HashCalculatorPage;

